import React, { useState, useRef, useEffect } from 'react';
import Container from '../../../layouts/Container';
import FilterTableWidget from '../../../components/FilterTableWidget';
import Toaster from '../../../components/Toaster';
import Modal from '../../../components/Modal';
import { useAuth } from '../../../providers/AuthContext';
import { useAppContext } from '../../../providers/AppContext';
import { useNavigate } from 'react-router-dom';
import { getTransactions, uploadTransactionsFile, getUploadHistory, deleteTransactionBatch } from '../../../services/apiTransactions';
import { downloadTransactionsTemplate } from '../../../utils/downloadTransactionsTemplate';
import ResumenHistoricoWidget from '../components/ResumenHistoricoWidget';

const Transactions = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [toastConfig, setToastConfig] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [batchToDelete, setBatchToDelete] = useState(null);
  const fileInputRef = useRef(null);
  const resumenRef = useRef(null);
  const { auth, hasPermission } = useAuth();
  const { addToast } = useAppContext();
  const navigate = useNavigate();

  const canImport = hasPermission("accionistas_transactions", "import", "create");

  const fetchTransactions = () => {
    if (!auth?.token) return Promise.resolve();
    setLoading(true);
    return getTransactions(auth.token)
      .then(res => {
        // Mapear los datos para la tabla (las columnas originales)
        const mappedData = res.map(t => ({
          Fecha: t.date ? new Date(t.date).toLocaleDateString('es-ES', { timeZone: 'UTC' }) : '',
          'Grupo Familiar': t.grupo_familiar,
          Cédula: t.cedula,
          Accionista: t.nombre_accionista,
          Detalle: t.detalle,
          Clasificación: t.clasificacion,
          'Tipo Movimiento': t.tipo_movimiento,
          Valor: t.valor,
        }));
        setData(mappedData);
      })
      .catch(error => {
        if ([403].includes(error.response?.status)) {
          addToast("No autorizado", 2);
          navigate('/logout');
        } else if ([419].includes(error.response?.status)) {
          addToast("Sesión expirada", 1);
          navigate('/logout');
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchTransactions();
  }, [auth]);

  const isUploading = useRef(false);

  const handleFileUpload = async (event) => {
    if (isUploading.current) return;

    const file = event.target.files[0];
    if (!file) return;

    // Limpiar el input inmediatamente para evitar eventos duplicados y permitir seleccionar el mismo archivo después
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    isUploading.current = true;
    setLoading(true);
    try {
      const response = await uploadTransactionsFile(file, auth.token);
      await fetchTransactions();
      resumenRef.current?.refresh();

      if (response && response.totalRecords === 0) {
        setToastConfig({ message: "Archivo sin registros procesados. Eliminando carga", type: 3 });
        if (response.uploadBatchId) {
          try {
             await deleteTransactionBatch(response.uploadBatchId, auth.token);
          } catch(e) {
             console.error("Error al auto-eliminar carga vacía", e);
          }
        }
      } else {
        setToastConfig({ message: "Excel Importado", type: 0 });
      }
    } catch (error) {
      console.error("Error al subir el archivo:", error);
      const errorMsg = error.message || "Hubo un error al procesar el archivo.";
      if (errorMsg.includes("fue procesado recientemente") || errorMsg.includes("espera unos minutos antes de volver a cargarlo para evitar duplicados")) {
        setToastConfig({ message: "Se está cargando un mismo archivo de los últimos 5 minutos", type: 2 });
      } else {
        setToastConfig({ message: errorMsg, type: 3 });
      }
    } finally {
      setLoading(false);
      isUploading.current = false;
      if (showHistory) {
        fetchHistory();
      }
    }
  };

  const fetchHistory = async () => {
    try {
      setLoadingHistory(true);
      const res = await getUploadHistory(auth.token);
      setHistoryData(res);
    } catch (error) {
      console.error("Error al obtener el historial:", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const requestDeleteBatch = (batchId) => {
    setBatchToDelete(batchId);
    setDeleteModalOpen(true);
  };

  const confirmDeleteBatch = async () => {
    if (!batchToDelete) return;
    const batchId = batchToDelete;
    setDeleteModalOpen(false);

    setLoadingHistory(true);
    try {
      await deleteTransactionBatch(batchId, auth.token);
      await fetchHistory(); // Refresh history
      await fetchTransactions(); // Refresh transactions table
      resumenRef.current?.refresh(); // Refresh resumen
      setToastConfig({ message: "Registro Eliminado", type: 0 });
    } catch (error) {
      console.error("Error al eliminar lote:", error);
      setToastConfig({ message: error.message || "Hubo un error al eliminar el lote.", type: 3 });
      setLoadingHistory(false);
    } finally {
      setBatchToDelete(null);
    }
  };

  const toggleHistory = () => {
    if (!showHistory) {
      fetchHistory();
    }
    setShowHistory(!showHistory);
  };

  return (
    <Container>
      {toastConfig && (
        <Toaster
          message={toastConfig.message}
          type={toastConfig.type}
          onClose={() => setToastConfig(null)}
        />
      )}
      <div className="w-11/12 mx-auto p-4 my-2">

        <div className="mb-6 flex items-center w-full">
          <div className="flex items-center gap-4">
            {canImport && (
              <>
                <input
                  type="file"
                  accept=".xlsx, .xls"
                  onChange={handleFileUpload}
                  className="hidden"
                  ref={fileInputRef}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="bg-[#10174c] hover:bg-[#10174c]/90 text-white font-semibold py-2 px-4 rounded shadow transition"
                  disabled={loading}
                >
                  {loading ? "Procesando..." : "Importar Excel"}
                </button>
                <button
                  onClick={toggleHistory}
                  className="bg-[#14b8a6] hover:bg-[#14b8a6]/90 text-white font-semibold py-2 px-4 rounded shadow transition"
                >
                  {showHistory ? "Ocultar Historial" : "Ver Historial"}
                </button>
                <button
                  className="bg-[#10174c] hover:bg-[#10174c]/90 text-white font-semibold py-2 px-4 rounded shadow transition"
                  onClick={downloadTransactionsTemplate}
                >
                  Descargar Plantilla
                </button>
              </>
            )}
          </div>

        </div>

        {showHistory && (
          <div className="mb-6 bg-white p-4 rounded shadow">
            <h3 className="text-lg font-semibold text-[#10174c] mb-1">Historial de Cargas</h3>
            <p className="text-sm text-gray-500 mb-4">Los archivos cargados se muestran en el historial durante 30 días. Al finalizar ese período, dejan de aparecer en el historial, aunque sus registros continúan disponibles</p>
            {loadingHistory ? (
              <p className="text-sm text-gray-500">Cargando historial...</p>
            ) : historyData.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left text-gray-600">
                  <thead className="text-xs text-gray-700 uppercase bg-gray-100">
                    <tr>
                      <th className="px-4 py-2">Fecha y Hora</th>
                      <th className="px-4 py-2">Archivo Original</th>
                      <th className="px-4 py-2 text-center">Usuario</th>
                      <th className="px-4 py-2 text-center">Registros Procesados</th>
                      <th className="px-4 py-2 text-center">Registros No Procesados</th>
                      <th className="px-4 py-2 text-center">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyData.map((hist) => (
                      <tr key={hist._id} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-2">
                          {new Date(hist.upload_timestamp).toLocaleString()}
                        </td>
                        <td className="px-4 py-2 font-medium text-gray-900">
                          {hist.original_filename}
                        </td>
                        <td className="px-4 py-2 text-center">
                          {hist.upload_user_id ? hist.upload_user_id.username : 'Desconocido'}
                        </td>
                        <td className="px-4 py-2 text-center">
                          {hist.registros_procesados}
                        </td>
                        <td className="px-4 py-2 text-center">
                          {hist.registros_no_procesados ?? 0}
                        </td>
                        <td className="px-4 py-2 text-center">
                          <button
                            onClick={() => requestDeleteBatch(hist.upload_batch_id)}
                            className="text-red-500 hover:text-red-700 transition"
                            title="Eliminar registro"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No hay historial de cargas.</p>
            )}
          </div>
        )}
        <ResumenHistoricoWidget ref={resumenRef} />

        {data.length > 0 ? (
          <div className="mt-4">
             <FilterTableWidget
                title="Transacciones"
                data={data}
             />
          </div>
        ) : (
          <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded p-10 text-center text-gray-500">
            No hay datos para mostrar. Por favor, importa un archivo Excel.
          </div>
        )}
      </div>

      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Confirmar Eliminación"
        size="w-full max-w-md"
        colorTitle="bg-[#10174c]"
        customType={2}
      >
        <div className="p-4 text-center">
          <p className="text-gray-700 mb-6">
            Todos los registros asociados a este lote también serán eliminados de forma permanente. Esta acción no se puede deshacer.
          </p>
          <div className="flex justify-center gap-4">
            <button
              onClick={() => setDeleteModalOpen(false)}
              className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 transition"
            >
              Cancelar
            </button>
            <button
              onClick={confirmDeleteBatch}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
            >
              Eliminar
            </button>
          </div>
        </div>
      </Modal>


    </Container>
  );
};

export default Transactions;
